import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { SITE, HERO_IMAGE } from "@/lib/site";
import { maskUp, easeOut } from "@/lib/anim";

const LINE1 = ["SPACES", "THAT"];
const LINE2 = ["STAY", "SPOTLESS"];

const Word = ({ children, i, accent }) => (
  <span className="reveal-mask">
    <motion.span
      variants={maskUp}
      custom={i}
      initial="hidden"
      animate="show"
      className={`inline-block ${accent ? "outline-text-acid" : ""}`}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative border-b border-line pt-28 md:pt-32">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-6 items-end pb-10 md:pb-16">
          {/* Left — kinetic type */}
          <div className="lg:col-span-8">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.8 }}
              className="font-mono text-[11px] uppercase tracking-[0.3em] text-acid mb-6 md:mb-10"
            >
              ◆ Commercial cleaning · {SITE.serviceArea}
            </motion.p>

            <h1 className="font-display font-black leading-[0.86] tracking-tighter text-[16vw] sm:text-[13vw] lg:text-[9.2vw]">
              <span className="block">
                {LINE1.map((w, i) => (
                  <span key={w} className="mr-[0.15em]"><Word i={i}>{w}</Word></span>
                ))}
              </span>
              <span className="block">
                {LINE2.map((w, i) => (
                  <span key={w} className="mr-[0.15em]">
                    <Word i={i + 2} accent={w === "SPOTLESS"}>{w}</Word>
                  </span>
                ))}
              </span>
            </h1>

            <div className="mt-8 md:mt-12 grid grid-cols-1 sm:grid-cols-12 gap-6 items-start">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.7, ease: easeOut }}
                className="sm:col-span-6 text-white/60 text-base md:text-lg leading-relaxed max-w-md"
              >
                {SITE.brandFull} runs documented, audited janitorial programs for offices, high-rises and industrial sites — so your building always looks on-duty.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.85, duration: 0.7, ease: easeOut }}
                className="sm:col-span-6 sm:justify-self-end"
              >
                <a
                  href="#quote"
                  data-testid="hero-quote-cta"
                  onClick={(e) => { e.preventDefault(); document.querySelector("#quote")?.scrollIntoView({ behavior: "smooth" }); }}
                  className="group inline-flex items-center gap-4 bg-acid text-ink-900 pl-7 pr-5 py-4 font-mono text-xs uppercase tracking-[0.2em] font-bold hover:bg-acid-hover transition-colors"
                >
                  Request a quote
                  <span className="grid place-items-center h-8 w-8 border border-ink-900/40 group-hover:rotate-45 transition-transform duration-300">
                    <ArrowDownRight size={16} />
                  </span>
                </a>
              </motion.div>
            </div>
          </div>

          {/* Right — clipped parallax image */}
          <div className="lg:col-span-4">
            <motion.div
              initial={{ clipPath: "inset(100% 0 0 0)" }}
              animate={{ clipPath: "inset(0% 0 0 0)" }}
              transition={{ delay: 0.4, duration: 1.1, ease: easeOut }}
              className="relative aspect-[3/4] overflow-hidden border border-line"
            >
              <motion.img
                src={HERO_IMAGE}
                alt="Commercial cleaning in progress at a ground-floor office entrance"
                style={{ y, scale: scaleImg }}
                className="absolute inset-0 h-[120%] w-full object-cover grayscale contrast-125"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-900/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 flex items-end justify-between font-mono text-[10px] uppercase tracking-[0.2em] text-white/80">
                <span>Est. {SITE.established}</span>
                <span className="text-acid">Bonded · Insured</span>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
